/**
 * Primitivos de interfaz.
 *
 * Sustituyen a las cadenas de clases Tailwind copiadas y pegadas en cada
 * plantilla Jinja: el estilo de un botón o una tarjeta se define una vez aquí.
 */

import clsx from 'clsx';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';
import { forwardRef } from 'react';

// --------------------------------------------------------------------------- //
// Botón
// --------------------------------------------------------------------------- //
type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-green text-white hover:bg-brand-green-dark shadow-card',
  secondary: 'bg-brand-blue text-white hover:bg-brand-blue-dark shadow-card',
  danger: 'bg-brand-red text-white hover:bg-red-600 shadow-card',
  ghost: 'bg-transparent text-slate-600 hover:bg-slate-100',
  outline: 'border-2 border-slate-200 bg-white text-slate-700 hover:border-slate-300',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-5 py-2.5 text-sm',
  lg: 'px-7 py-3.5 text-base',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    isLoading,
    fullWidth,
    className,
    children,
    disabled,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      // `disabled` mientras carga evita el doble envío de formularios y de respuestas.
      disabled={disabled || isLoading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-2xl font-bold uppercase tracking-wide',
        'transition-all duration-150 focus-visible:outline focus-visible:outline-2',
        'focus-visible:outline-offset-2 focus-visible:outline-brand-blue',
        'disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {isLoading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
});

// --------------------------------------------------------------------------- //
// Campos de formulario
// --------------------------------------------------------------------------- //
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, className, id, ...props },
  ref,
) {
  const inputId = id ?? props.name;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-bold text-slate-700">
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={clsx(
          'w-full rounded-2xl border-2 bg-white px-4 py-3 text-slate-800 transition-colors',
          'placeholder:text-slate-400 focus:outline-none',
          error
            ? 'border-brand-red focus:border-brand-red'
            : 'border-slate-200 focus:border-brand-blue',
          className,
        )}
        {...props}
      />
      {error ? (
        <p id={`${inputId}-error`} role="alert" className="text-sm font-medium text-brand-red">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="text-sm text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, className, id, children, ...props },
  ref,
) {
  const selectId = id ?? props.name;
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={selectId} className="block text-sm font-bold text-slate-700">
          {label}
        </label>
      )}
      <select
        ref={ref}
        id={selectId}
        aria-invalid={error ? true : undefined}
        className={clsx(
          'w-full rounded-2xl border-2 bg-white px-4 py-3 text-slate-800',
          'focus:outline-none',
          error ? 'border-brand-red' : 'border-slate-200 focus:border-brand-blue',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {error && (
        <p role="alert" className="text-sm font-medium text-brand-red">
          {error}
        </p>
      )}
    </div>
  );
});

// --------------------------------------------------------------------------- //
// Contenedores y estados
// --------------------------------------------------------------------------- //
export function Card({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div
      className={clsx(
        'rounded-3xl border-2 border-surface-border bg-white shadow-card',
        padded && 'p-6',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Cargando"
      className={clsx(
        'inline-block animate-spin rounded-full border-2 border-current border-t-transparent',
        className ?? 'h-5 w-5',
      )}
    />
  );
}

export function PageLoader({ label = 'Cargando…' }: { label?: string }) {
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 text-slate-500">
      <Spinner className="h-8 w-8 text-brand-green" />
      <p className="font-medium">{label}</p>
    </div>
  );
}

export function Alert({
  variant = 'error',
  children,
}: {
  variant?: 'error' | 'success' | 'info' | 'warning';
  children: ReactNode;
}) {
  const styles = {
    error: 'border-brand-red/30 bg-red-50 text-red-800',
    success: 'border-brand-green/30 bg-green-50 text-green-800',
    info: 'border-brand-blue/30 bg-blue-50 text-blue-800',
    warning: 'border-brand-orange/30 bg-orange-50 text-orange-800',
  }[variant];

  return (
    <div
      role="alert"
      className={clsx('rounded-2xl border-2 px-4 py-3 text-sm font-medium', styles)}
    >
      {children}
    </div>
  );
}

export function EmptyState({
  icon = '🕊️',
  title,
  description,
  action,
}: {
  icon?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-3xl border-2 border-dashed border-slate-200 px-6 py-12 text-center">
      <span className="text-4xl" aria-hidden>
        {icon}
      </span>
      <h3 className="text-lg font-bold text-slate-700">{title}</h3>
      {description && <p className="max-w-md text-sm text-slate-500">{description}</p>}
      {action}
    </div>
  );
}

export function ProgressBar({
  value,
  className,
  label,
}: {
  value: number;
  className?: string;
  label?: string;
}) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label ?? 'Progreso'}
      className={clsx('h-4 w-full overflow-hidden rounded-full bg-slate-200', className)}
    >
      <div
        className="h-full rounded-full bg-brand-green transition-[width] duration-500 ease-out"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

export function Badge({
  children,
  color = 'green',
}: {
  children: ReactNode;
  color?: 'green' | 'blue' | 'purple' | 'orange' | 'red' | 'slate';
}) {
  const styles = {
    green: 'bg-green-100 text-green-800',
    blue: 'bg-blue-100 text-blue-800',
    purple: 'bg-purple-100 text-purple-800',
    orange: 'bg-orange-100 text-orange-800',
    red: 'bg-red-100 text-red-800',
    slate: 'bg-slate-100 text-slate-700',
  }[color];

  return (
    <span className={clsx('rounded-full px-3 py-1 text-xs font-bold', styles)}>{children}</span>
  );
}

export function StatTile({
  label,
  value,
  hint,
  icon,
}: {
  label: string;
  value: string | number;
  hint?: string;
  icon?: string;
}) {
  return (
    <Card className="flex items-center gap-4">
      {icon && (
        <span className="text-3xl" aria-hidden>
          {icon}
        </span>
      )}
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-500">{label}</p>
        <p className="text-2xl font-extrabold text-slate-800">{value}</p>
        {hint && <p className="text-xs text-slate-400">{hint}</p>}
      </div>
    </Card>
  );
}
